import dispatcher from "../dispatcher"
import * as api from "../api"
import strings from "../localization/strings"
import { EPunchValidationType, IPunchBundle, IPosition, IEditablePunchData, IAvailablePositions, IPunchSetDocument } from "../models"
import AccountStore from "../stores/AccountStore"
import AppStateStore from "../stores/AppStateStore"
import * as hierarchyHelper from "../helpers/hierarchyHelper"
import * as utils from "../helpers/utils"
import * as firebaseActions from "./FirebaseActions"
import EditablePunchTableDataStore from "../stores/EditablePunchTableDataStore"
import * as userHelper from "../helpers/userHelper"
import { checkPhaseFieldAvailable } from "../helpers/punchHelper"
import accountStore from "../stores/AccountStore"

export function patchPunch(userId: string, punchId: string, punch, updatePunchPosition: boolean, punchPosition: IPosition, allowPriorPeriodModifications: boolean, successCb: (msg: string) => void, errorCb: () => void = null) {
    let punchBundle = {
        Punch: punch,
        PunchPosition: {
            Position: punchPosition
        }
    }
    AppStateStore.setProgressSnackbarContent("loading")
    let msg = null
    api.patchPunch(punchId, updatePunchPosition, punchBundle, allowPriorPeriodModifications || false, userHelper.isESSAction(userId)).then(res => {
        switch (res.data as EPunchValidationType) {
            case EPunchValidationType.Valid:
                msg = strings.Successfully_updated
                break
            case EPunchValidationType.InvalidHierarchy:
                msg = strings.Punch_hierarchy_is_invalid
                break
            case EPunchValidationType.NotAllowedByHierarchyDefinition:
                const hierarchyDefinition = hierarchyHelper.getHierarchyDefinitionByHierarchyItemId(punch.HierarchyItemId)
                msg = hierarchyDefinition.MessageForDisablePunches
                break
            case EPunchValidationType.NotAllowedByLockedPeriod:
                msg = strings.Failed_to_update_punch_for_lock_period
                break
            case EPunchValidationType.NotAllowedForAccount:
                msg = AccountStore.getCurrentAccountSettings().MessageForDisablePunches
                break
            case EPunchValidationType.SplitPunchError:
                msg = strings.Cannot_update_auto_created_split_punch
                break
            case EPunchValidationType.OverriddenDayGroupPunchSetMaxLengthError:
                msg = strings.Failed_to_update_punch_time_for_max_length
                break
            case EPunchValidationType.NotAllowedOverrideDayGroupForSpecificPS:
                msg = strings.Cannot_change_the_calculation_day_grouping_for_special_punch_set
                break
            case EPunchValidationType.NotAllowedCalcualtionDayByLockedPeriod:
                msg = strings.Calculation_Day_cannot_fall_in_locked_period
                break
            case EPunchValidationType.EmptyPhase:
                msg = checkPhaseFieldAvailable("singular") ? `Punch has no ${accountStore.getCurrentAccountSettings().CustomFieldSettings.PhaseFieldLabels.SingularPhaseLabel}`:  strings.Punch_has_not_phase
                break
            case EPunchValidationType.InvalidAssignedJob:
                msg = strings.Punch_is_incorrect_against_assigning_jobs
                break
            case EPunchValidationType.NotAllowedByEmployeePermission:
                msg = strings.Punch_hierarchy_is_not_valid_by_employee_permission
                break
        }
        if (res.data === EPunchValidationType.Valid) {
            successCb(msg)
        }
        else {
            AppStateStore.setProgressSnackbarContent(msg)
            dispatcher.dispatch({ type: "FAILED_PATCH_PUNCH" })
            errorCb &&
                errorCb()
        }
    })
}

export function deletePunchHandler(punchId: string, punchSet: IPunchSetDocument, allowPriorPeriodChange: boolean, successCb: (msg: string) => void) {
    if (punchSet.TimeOffRequestId) {
        AppStateStore.setProgressSnackbarContent(strings.Cannot_delete_leave_request_punch)
        return
    }
    AppStateStore.setConfirmDialogContent({
        title: punchSet.PreQuestionGUIds && punchSet.PreQuestionGUIds.length > 0 ? strings.Delete_Question_Answer : strings.Delete_Punch.toUpperCase(),
        content:
            punchSet.PreQuestionGUIds && punchSet.PreQuestionGUIds.length > 0 ?
                strings.Are_you_sure_you_want_to_delete_this_question_answer :
                punchSet.OverrideDayGroupingTime ?
                    strings.Deleting_this_punch_will_delete_the_full_punch_set_Proceed :
                    strings.Are_you_sure_delete_Punch,
        confirm: () => deletePunch(punchSet.UserId, punchId, allowPriorPeriodChange, successCb)
    })
}

export async function deletePunch(userId: string, punchId: string, allowPriorPeriodChange: boolean, successCb: (msg: string) => void) {
    AppStateStore.setProgressSnackbarContent("loading")
    const res = await api.deletePunch(punchId, allowPriorPeriodChange || false, userHelper.isESSAction(userId))
    let msg = null
    switch (res.data as EPunchValidationType) {
        case EPunchValidationType.Valid:
            msg = strings.Successfully_deleted
            break
        case EPunchValidationType.NotAllowedByLockedPeriod:
            msg = strings.Failed_to_delete_punch
            break
        case EPunchValidationType.SplitPunchError:
            msg = strings.Cannot_delete_auto_created_split_punch
            break
        case EPunchValidationType.AlreadyDeleted:
            msg = strings.Punch_was_deleted_already
            break
    }
    if (res.data === EPunchValidationType.Valid || res.data === EPunchValidationType.AlreadyDeleted) {
        successCb(msg)
    }
    else {
        AppStateStore.setProgressSnackbarContent(msg)
    }
}

export function addPunch(userId: string, punchBundle: IPunchBundle, ignoreNotAllowedPunchSetting: boolean, successCb: (msg: string) => void = null, isWebPunch = false, ipAddress = "", allowPriorPeriodChange: boolean = false) {

    AppStateStore.setProgressSnackbarContent("loading")
    if (punchBundle.Punch.PhaseId && !AccountStore.getPhasesEnabled()) {
        punchBundle.Punch.PhaseId = null
        punchBundle.Punch.PhaseName = null
        punchBundle.Punch.PhaseCostCode = null
    }

    if (punchBundle.Punch.JobId && !AccountStore.getJobsEnabled()) {
        punchBundle.Punch.JobId = null
        punchBundle.Punch.JobName = null
        punchBundle.Punch.JobCostCode = null
        punchBundle.Punch.PhaseId = null
        punchBundle.Punch.PhaseName = null
        punchBundle.Punch.PhaseCostCode = null
    }

    api.addPunch(userId, punchBundle, ignoreNotAllowedPunchSetting, isWebPunch, ipAddress, allowPriorPeriodChange || false, userHelper.isESSAction(userId))
        .then(res => {
            AppStateStore.triggerGetFreeAccountMetricsData()
            let msg = null
            switch (res.data as EPunchValidationType) {
                case EPunchValidationType.Valid:
                    msg = strings.Punch_added_successfully
                    break
                case EPunchValidationType.InvalidHierarchy:
                    msg = strings.Punch_hierarchy_is_invalid
                    break
                case EPunchValidationType.NotAllowedByHierarchyDefinition:
                    const hierarchyDefinition = hierarchyHelper.getHierarchyDefinitionByHierarchyItemId(punchBundle.Punch.HierarchyItemId)
                    msg = hierarchyDefinition.MessageForDisablePunches
                    break
                case EPunchValidationType.NotAllowedByLockedPeriod:
                    msg = strings.Cannot_punch_for_locked_pay_period
                    break
                case EPunchValidationType.NotAllowedForAccount:
                    msg = AccountStore.getCurrentAccountSettings().MessageForDisablePunches
                    break
                case EPunchValidationType.InactiveEmployeeSuspensePunch:
                    msg = strings.Employee_is_not_an_active_employee_This_punch_is_now_in_Suspense_and_can_be_found_in_the_Records_tab
                    break
                case EPunchValidationType.SuspensePunch:
                    msg = strings.This_punch_is_now_in_Suspense_and_can_be_found_in_the_Records_tab
                    break
                case EPunchValidationType.InvalidIPAddress:
                    msg = strings.formatString(strings.Cannot_web_punch_The_IP_Address_is_invalid, ipAddress) as string
                    break
                case EPunchValidationType.EmptyPhase:
                    msg = checkPhaseFieldAvailable("singular") ? `Punch has no ${accountStore.getCurrentAccountSettings().CustomFieldSettings.PhaseFieldLabels.SingularPhaseLabel}`:  strings.Punch_has_not_phase
                    break
                case EPunchValidationType.FreeAccountLimitSuspensePunch:
                    msg = null
                    break
                case EPunchValidationType.InvalidAssignedJob:
                    msg = strings.Punch_is_incorrect_against_assigning_jobs
                    break
                case EPunchValidationType.NotAllowedByEmployeePermission:
                    msg = strings.Punch_hierarchy_is_not_valid_by_employee_permission
                    break
            }
            if (res.data === EPunchValidationType.Valid) {
                successCb(msg)
            }
            else {
                AppStateStore.setProgressSnackbarContent(msg)
                res.data === EPunchValidationType.FreeAccountLimitSuspensePunch &&
                    AppStateStore.setFreeAccountDataLimitUpgradeDialogContent(strings.The_punch_count_reached_to_1000_limit_for_free_account_This_punch_was_in_suspense_state_and_not_added_to_timecard_Please_upgrade_your_account_plan_to_add_this_punch_to_timecard)
            }
        })
        .catch(e =>
            e.response.data.message &&
            AppStateStore.setProgressSnackbarContent(e.response.data.message)
        )
}

export async function addGlobalPunch(userIds: string[], punchBundle: IPunchBundle) {
    const asyncSessionId = utils.guid()
    setTimeout(() =>
        dispatcher.dispatch({
            type: "UPDATE_ASYNC_SESSION_ID",
            data: asyncSessionId
        })
        , 0)
    var result = await api.addGlobalPunch(userIds, punchBundle, asyncSessionId)
    firebaseActions.clearAsyncSessionId()
    return result.data
}

export async function editGlobalPunch(punchBundles: IPunchBundle[]) {
    const asyncSessionId = utils.guid()
    setTimeout(() =>
        dispatcher.dispatch({
            type: "UPDATE_ASYNC_SESSION_ID",
            data: asyncSessionId
        })
        , 0)
    var result = await api.editGlobalPunch(punchBundles, asyncSessionId)
    firebaseActions.clearAsyncSessionId()
    return result.data
}


export function initEditablePunchStore() {
    EditablePunchTableDataStore.initEditablePunchData()
}

export async function getUserEditablePunchData(userId: string, startDateTime: string, endDateTime: string) {
    const res = await api.getUserEditablePunchData(userId, startDateTime, endDateTime)
    const editablePunchData: IEditablePunchData = res.data
    EditablePunchTableDataStore.receiveUserEditablePunchData(editablePunchData)
    return { punchBundle: editablePunchData.PunchBundles, lockedPeriods: editablePunchData.LockedPeriods }
}

export async function getJob(jobId: string, useEditablePunchTableDataStore: boolean) {
    if (useEditablePunchTableDataStore) {
        var storedJob = EditablePunchTableDataStore.getJob(jobId)
        if (storedJob)
            return storedJob
    }
    const res = await api.getJob(jobId)
    useEditablePunchTableDataStore &&
        EditablePunchTableDataStore.addJob(res.data)
    return res.data
}

export async function getEquipment(equipmentId: string, useEditablePunchTableDataStore: boolean) {
    if (useEditablePunchTableDataStore) {
        var storedEquipment = EditablePunchTableDataStore.getEquipment(equipmentId)
        if (storedEquipment)
            return storedEquipment
    }
    const res = await api.getEquipment(equipmentId)
    useEditablePunchTableDataStore &&
        EditablePunchTableDataStore.addEquipment(res.data)
    return res.data
}

export async function getShift(shiftId: string, useEditablePunchTableDataStore: boolean) {
    if (useEditablePunchTableDataStore) {
        var storedShift = EditablePunchTableDataStore.getShift(shiftId)
        if (storedShift)
            return storedShift
    }
    const res = await api.getShift(shiftId)
    useEditablePunchTableDataStore &&
        EditablePunchTableDataStore.addShift(res.data)
    return res.data
}

export async function getPositions(userId: string, hierarchyId: string, shiftId: string, jobId: string, phaseId: string, useEditablePunchTableDataStore: boolean) {
    if (useEditablePunchTableDataStore) {
        var storedAvailablePosition = EditablePunchTableDataStore.getAvailablePosition(userId, shiftId, hierarchyId, jobId, phaseId)

        if (storedAvailablePosition)
            return { userId, hierarchyId, shiftId, jobId, phaseId, positions: storedAvailablePosition }
    }
    const res = await api.getPositions(userId, hierarchyId, shiftId, jobId, phaseId)
    const positions: IPosition[] = res.data
    if (useEditablePunchTableDataStore) {
        const availablePosition: IAvailablePositions = {
            UserId: userId,
            HierarchyId: hierarchyId,
            ShiftId: shiftId,
            JobId: jobId,
            PhaseId: phaseId,
            Positions: positions
        }
        EditablePunchTableDataStore.addAvailablePositions(availablePosition)
    }

    return { userId, hierarchyId, shiftId, jobId, phaseId, positions }
}

export async function getPunchPosition(punchId: string, useEditablePunchTableDataStore: boolean) {
    if (useEditablePunchTableDataStore) {
        var storedPunchPosition = EditablePunchTableDataStore.getPunchPositionDocument(punchId)
        if (storedPunchPosition)
            return storedPunchPosition
    }
    const res = await api.getPunchPosition(punchId)
    return res.data
}

export async function recalcRecacheTimecards(recalc: boolean, referenceDate: string, allTimecards: boolean, excludeInactiveEmployees: boolean, payrollHierarchyIds: string[]) {
    const asyncSessionId = utils.guid()
    setTimeout(() =>
        dispatcher.dispatch({
            type: "UPDATE_ASYNC_SESSION_ID",
            data: asyncSessionId
        })
        , 0)
    var result = await api.recalcRecacheTimecards(AccountStore.getCurrentAccount().Id, recalc, referenceDate, allTimecards, excludeInactiveEmployees, payrollHierarchyIds, asyncSessionId)
    firebaseActions.clearAsyncSessionId()
    return result.data
}